import { action } from "typesafe-actions";
import { IEvent } from "./events.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import { showMessage } from "../messages/messagesActions";
import { endPoints } from "../../constants/endPoints";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { formatDate2 } from "../../helpers";

// Fetch Events List Actions
export const FETCH_EVENTS_LIST_PROGRESS = "FETCH_EVENTS_LIST_PROGRESS";
export const FETCH_EVENTS_LIST_SUCCESS = "FETCH_EVENTS_LIST_SUCCESS";
export const FETCH_EVENTS_LIST_FAILED = "FETCH_EVENTS_LIST_FAILED";

export const fetchEventsListProgress = () => action(FETCH_EVENTS_LIST_PROGRESS);
export const fetchEventsListSuccess = (list: IEvent[], totalRecords: number) =>
  action(FETCH_EVENTS_LIST_SUCCESS, { list, totalRecords });
export const fetchEventsListFailed = () => action(FETCH_EVENTS_LIST_FAILED);

export const fetchEventsListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter,
    companyOutletUuid: string,
    searchByName: string = "",
    searchByEventType: string = "",
    searchByLocation: string = ""
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchEventsListProgress());

      let url = "";
      if (status !== "-1") {
        url += `&status=${status}`;
      }
      if (companyOutletUuid) {
        url += `&company_outlet_uuid=${companyOutletUuid}`;
      }
      if (searchValue.length > 0) {
        url += `&advanceFilter=${JSON.stringify(searchValue)}`;
      }

      // Initialize filters array for additional search parameters
      const filters = [];

      // Add name filter
      if (searchByName) {
        filters.push({
          column: ["name"],
          value: searchByName,
          logicalOperator: "AND",
          operator: "CONTAINS",
        });
      }

      // Add event type filter
      if (searchByEventType) {
        filters.push({
          column: ["event_type"],
          value: searchByEventType,
          logicalOperator: "AND",
          operator: "CONTAINS",
        });
      }

      // Add location filter
      if (searchByLocation) {
        filters.push({
          column: ["location"],
          value: searchByLocation,
          logicalOperator: "AND",
          operator: "CONTAINS",
        });
      }

      // Add filters to URL if any exist
      if (filters.length > 0) {
        url += `&advanceFilter=${JSON.stringify(filters)}`;
      }

      let from_date = formatDate2(date.fromDate);
      let to_date = formatDate2(date.toDate);

      let finalUrl =
        `${endPoints.events.getEventType}?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&from_date=${from_date}&to_date=${to_date}` +
        url;

      if (searchValue.length > 0 || status !== "-1" || filters.length > 0) {
        finalUrl =
          `${endPoints.events.getEventType}?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}` +
          url;
      }

      const res = await api.get(finalUrl);
      const data: IEvent[] = res.data.data.map((event: any) => ({
        ...event,
        is_registered: Boolean(event.is_registered)
      }));
      const totalRecords = res.data.totalRecords;

      dispatch(fetchEventsListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchEventsListFailed());
      dispatch(
        showMessage({
          message: err?.response?.data?.message || "Failed to fetch events",
          type: "error",
          displayAs: "snackbar",
        })
      );
    }
  };

// Fetch Single Event Actions
export const FETCH_EVENT_PROGRESS = "FETCH_EVENT_PROGRESS";
export const FETCH_EVENT_SUCCESS = "FETCH_EVENT_SUCCESS";
export const FETCH_EVENT_FAILED = "FETCH_EVENT_FAILED";

export const fetchEventProgress = () => action(FETCH_EVENT_PROGRESS);
export const fetchEventSuccess = (event: IEvent) =>
  action(FETCH_EVENT_SUCCESS, { event });
export const fetchEventFailed = (errorMessage: string) =>
  action(FETCH_EVENT_FAILED, { errorMessage });

export const fetchEventAsync =
  (eventId: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchEventProgress());

      const res = await api.get(`${endPoints.events.getEventType}/${eventId}`);
      const data: IEvent = {
        ...res.data.data,
        is_registered: Boolean(res.data.data.is_registered)
      };

      dispatch(fetchEventSuccess(data));
    } catch (err: any) {
      dispatch(
        fetchEventFailed(
          err?.response?.data?.message || "Failed to fetch event"
        )
      );
      dispatch(
        showMessage({
          message: err?.response?.data?.message || "Failed to fetch event",
          type: "error",
          displayAs: "snackbar",
        })
      );
    }
  };

// Upsert Event Actions
export const UPSERT_EVENT_PROGRESS = "UPSERT_EVENT_PROGRESS";
export const UPSERT_EVENT_SUCCESS = "UPSERT_EVENT_SUCCESS";
export const UPSERT_EVENT_FAILED = "UPSERT_EVENT_FAILED";

export const upsertEventProgress = () => action(UPSERT_EVENT_PROGRESS);
export const upsertEventSuccess = (event: IEvent) =>
  action(UPSERT_EVENT_SUCCESS, { event });
export const upsertEventFailed = (errorMessage: string) =>
  action(UPSERT_EVENT_FAILED, { errorMessage });

export const upsertEventAsync =
  (
    eventData: IEvent,
    isUpdate: boolean,
    onCallBack: (isSuccess?: boolean, data?: IEvent) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(upsertEventProgress());

      const {
        create_ts,
        insert_ts,
        event_type_unique_id,
        ...payload
      } = eventData;
      
      const res = await api.post(endPoints.events.upsertEventType, payload);
      
      const data: IEvent = res.data.data;
      dispatch(upsertEventSuccess(data));
      dispatch(
        showMessage({
          message: isUpdate
            ? "Event updated successfully"
            : "Event created successfully",
          type: "success",
          displayAs: "snackbar",
        })
      );
      onCallBack(true, data);
    } catch (err: any) {
      dispatch(
        upsertEventFailed(
          err?.response?.data?.message || "Failed to save event"
        )
      );
      dispatch(
        showMessage({
          message: err?.response?.data?.message || "Failed to save event",
          type: "error",
          displayAs: "snackbar",
        })
      );
      onCallBack(false);
    }
  };

// Clear Actions
export const CLEAR_EVENT_STATE = "CLEAR_EVENT_STATE";
export const CLEAR_EVENT = "CLEAR_EVENT";

export const clearEventsState = () => action(CLEAR_EVENT_STATE);
export const clearEvent = () => action(CLEAR_EVENT); 